import React, { useState, useContext } from 'react'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useHistory, useParams } from 'react-router-dom'
import useQueryString from '../../../hooks/use-query-string'
import useBackgroundsFromPackage from './use-backgrounds-from-package'
import useSeniorImages from './use-senior-images'
import useImageTypesFromOption from './use-image-types-from-option'
import usePackage from './use-package'
import useOption from './use-option'
import PackageBuilderHelper from '../../package-builder-helper'
import SeniorPackageInstructions from '../../senior-package-instructions'
import { OrderPackage } from '../../../models'
import { CustomerContext } from '../../../contexts/CustomerContext'
import Typography from '../../Typography'
import { routes } from '../../../routes'

const PackageBuilder = () => {
  const history = useHistory()
  const { packageId } = useParams()
  const { customer, setCustomer } = useContext(CustomerContext)
  const { favs: favorites, opt: optionId } = useQueryString()
  const seniorPackage = usePackage(packageId)
  const backgrounds = useBackgroundsFromPackage(packageId)
  const studentImages = useSeniorImages(favorites)
  const imageTypes = useImageTypesFromOption(optionId)
  const option = useOption(optionId)
  const [step, setStep] = useState(0)
  const [startingIndex, setStartingIndex] = useState(0)
  const [sheets, setSheets] = useState([])
  const [yearbookImage, setYearbookImage] = useState()

  const handleYearbookSelection = (sheet) => {
    setYearbookImage({ ...sheet, background: backgrounds && backgrounds[0] ? backgrounds[0] : undefined, backgroundId: backgrounds && backgrounds[0] ? backgrounds[0].id : undefined })
  }

  const generateSeniorOrderPackage = async () => {
    const potentialExtraPoses = [...new Set(sheets.map((sheet) => sheet.seniorImageId))].length
    const extraPoses = Math.max(0, potentialExtraPoses - option.poses)

    const orderPackage = new OrderPackage({
      cartId: customer.cart.id,
      seniorImageId: yearbookImage.seniorImageId,
      studentId: customer.currentStudent.id,
      optionId: option.id,
      apiOption: option,
      packageId: seniorPackage.id,
      extraPoses,
      option,
      sheets,
    })

    const success = await orderPackage.save({ with: 'sheets' })

    if (!success) {
      toast('There was an error adding this item to your cart. Please refresh and try again.', { type: 'error' })
      return;
    }

    setCustomer({ ...customer, currentOrderPackage: orderPackage })

    const params = {
      favs: favorites,
      opt: optionId,
      package: seniorPackage.id,
      op: orderPackage.id,
    }

    history.push(routes.seniorAddons(params))
  }

  if (!(studentImages && seniorPackage && option && backgrounds && imageTypes)) return null

  return (
    <>
      <ToastContainer
        position="bottom-left"
      />
      {step === 0 && (
        <SeniorPackageInstructions
          title="Build Your Package"
          subtitle="Drag images to the sheets to build your package."
        />
      )}
      {step === 0 && (
        <PackageBuilderHelper
          seniors
          studentImages={studentImages}
          backgrounds={backgrounds}
          checkBackgrounds={studentImages.some((si) => si.imageFileName.includes('.png'))}
          selectedPackage={seniorPackage}
          selectedOption={option}
          sheetList={imageTypes}
          initialIndex={startingIndex}
          prefilledSheets={sheets}
          maxPoses={option.poses}
          onComplete={(newSheets) => {
            setSheets(newSheets)
            setStep(1)
          }}
        />
      )}
      {step === 1 && (
        <PackageBuilderHelper
          seniors
          checkBackgrounds={false}
          forwardText="Confirm Yearbook Pose"
          studentImages={studentImages}
          backgrounds={backgrounds}
          selectedPackage={seniorPackage}
          selectedOption={option}
          onBack={() => {
            setStep(0)
            setStartingIndex(imageTypes.length - 1)
          }}
          prefilledSheets={yearbookImage ? [yearbookImage] : []}
          onComplete={() => {
            setStep(2)
            generateSeniorOrderPackage()
          }}
          onSheetComplete={handleYearbookSelection}
          sheetList={[{ name: 'Yearbook Pose', count: 1 }]}
          maxPoses={1}
          foregroundMsg="If you have more than one pose, drag to choose your yearbook pose."
        />
      )}
      {step === 2 && (
        <Typography align="center">Creating your package...</Typography>
      )}
    </>
  )
}

export default PackageBuilder
